import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  TextField,
  IconButton,
  List,
  Fab,
  Grid,
  Typography,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Refresh, FilterList, Add, Check } from "@mui/icons-material";
import "../OrganizerTab.css";
import GuestCard from "./GuestCard";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import GuestModal from "./GuestModal";
import { useAuth } from "../../../contexts/AuthContext";
import { latinToCyrillic } from "../../../utils/latinToCyrillic";
import { PRODUCT_IDS } from "../../../utils/entitlements";

const API = process.env.REACT_APP_API_BASE;

const GuestsTab = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limitError, setLimitError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const { weddingId, hasProduct, entitlementsLoading, paymentsConfig } = useAuth();
  const [tables, setTables] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [relationshipFilter, setRelationshipFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [rsvpFilter, setRsvpFilter] = useState("all");
  const [seatedFilter, setSeatedFilter] = useState("all");
  const [sideFilter, setSideFilter] = useState("all");

  const fetchTables = async () => {
    try {
      const response = await fetchWithAuth(`${API}/get-tables`, {
        method: "POST",
      });
      if (!response.ok) throw new Error("Failed to fetch tables");
      const data = await response.json();
      setTables(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchGuests();
    fetchTables();
  }, []);

  const fetchGuests = async () => {
    try {
      setLoading(true);
      setError(null); // clear previous errors
      const response = await fetchWithAuth(`${API}/guests-grouped`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setGuests(data); // array of groups
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Грешка при вчитување на гости.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuests();
  }, []);

  const handleAdd = () => {
    if (limitReached) {
      setLimitError(
        `Достигнат е лимитот од ${guestLimit} гости. За повеќе, активирајте upgrade од ценовник.`
      );
      return;
    }
    setLimitError("");
    setSelectedGuest(null);
    setModalOpen(true);
  };

  const handleEdit = (groupData) => {
    setSelectedGuest(groupData);
    setModalOpen(true);
  };

  const handleDelete = (guestId) => {
    setGuests((prevGuests) =>
      prevGuests.filter((group) => !group.some((g) => g.id === guestId))
    );
  };

  const normalizedGroups = useMemo(() => {
    return guests
      .map((group) => (Array.isArray(group) ? group : []))
      .map((group) => {
        const mainGuest = group.find((g) => g.is_main_guest) || group[0];
        if (!mainGuest) {
          return null;
        }
        const sideGuests = group.filter((g) => g.id !== mainGuest.id);
        return { mainGuest, sideGuests };
      })
      .filter(Boolean);
  }, [guests]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleOpenFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const applyFilterUpdate = (updater) => {
    updater();
    setFilterAnchorEl(null);
  };

  const filteredGroups = useMemo(() => {
    const normalizeRelationship = (value) =>
      value === "COLLEAGUE" ? "COWORKER" : value;

    const trimmedQuery = searchQuery.trim().toLowerCase();
    const tokenVariants = trimmedQuery
      ? trimmedQuery
          .split(/\s+/)
          .map((token) => token.trim())
          .filter(Boolean)
          .map((token) => {
            const variants = new Set([token]);
            const cyr = latinToCyrillic(token);
            if (cyr) {
              variants.add(cyr);
            }
            return Array.from(variants);
          })
      : [];

    return normalizedGroups.filter(({ mainGuest, sideGuests }) => {
      if (
        relationshipFilter !== "all" &&
        normalizeRelationship(mainGuest.relationship_category) !==
          relationshipFilter
      ) {
        return false;
      }

      if (ageFilter !== "all" && mainGuest.age_category !== ageFilter) {
        return false;
      }

      if (rsvpFilter !== "all" && mainGuest.RSVP_status !== rsvpFilter) {
        return false;
      }

      if (seatedFilter === "seated" && !mainGuest.seated) {
        return false;
      }

      if (seatedFilter === "not-seated" && mainGuest.seated) {
        return false;
      }

      if (sideFilter !== "all" && mainGuest.side !== sideFilter) {
        return false;
      }

      if (!tokenVariants.length) {
        return true;
      }

      const names = [
        `${mainGuest.name ?? ""} ${mainGuest.last_name ?? ""}`,
        ...sideGuests.map(
          (guest) => `${guest.name ?? ""} ${guest.last_name ?? ""}`
        ),
      ].join(" ");

      const haystack = names.toLowerCase();
      const cyrillicHaystack = latinToCyrillic(haystack);

      return tokenVariants.every((variants) =>
        variants.some(
          (variant) =>
            haystack.includes(variant) || cyrillicHaystack.includes(variant)
        )
      );
    });
  }, [
    normalizedGroups,
    searchQuery,
    relationshipFilter,
    ageFilter,
    rsvpFilter,
    seatedFilter,
    sideFilter,
  ]);

  const isFilterMenuOpen = Boolean(filterAnchorEl);
  const hasActiveFilters =
    relationshipFilter !== "all" ||
    ageFilter !== "all" ||
    rsvpFilter !== "all" ||
    seatedFilter !== "all" ||
    sideFilter !== "all";
  const totalGuests = useMemo(
    () =>
      guests.reduce(
        (count, group) => count + (Array.isArray(group) ? group.length : 0),
        0
      ),
    [guests]
  );
  const hasUnlimitedGuestsProduct = hasProduct(PRODUCT_IDS.UNLIMITED_GUESTS);
  const guestLimit = Number.isFinite(Number(paymentsConfig?.default_guest_limit))
    ? Number(paymentsConfig.default_guest_limit)
    : 99;
  const limitReached =
    !hasUnlimitedGuestsProduct &&
    totalGuests >= guestLimit;

  return (
    <Grid
      item
      className="organizer-tab-container"
      size={{ xs: 10, md: 8, lg: 6, xl: 4 }}
    >
      <Box className="search-bar">
        <TextField
          fullWidth
          placeholder="Пребарувај"
          variant="outlined"
          size="small"
          className="search-input"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <IconButton color="inherit" onClick={fetchGuests}>
          <Refresh />
        </IconButton>
        <IconButton
          color={hasActiveFilters ? "primary" : "inherit"}
          onClick={handleOpenFilter}
        >
          <FilterList />
        </IconButton>
      </Box>
      {filteredGroups.length >= 3 ? (
        <Button className="quick-add-strip" endIcon={<Add />} onClick={handleAdd}>
          Додадете гости
        </Button>
      ) : null}
      {!entitlementsLoading && !hasUnlimitedGuestsProduct && (
        <Alert severity={limitReached ? "warning" : "info"} sx={{ mb: 2 }}>
          Гостински лимит без upgrade: {guestLimit}. Моментално имате{" "}
          {totalGuests}. За неограничени гости посетете{" "}
          <RouterLink to="/pricing">ценовник</RouterLink>.
        </Alert>
      )}
      {limitError ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLimitError("")}>
          {limitError}
        </Alert>
      ) : null}
      <Menu
        anchorEl={filterAnchorEl}
        open={isFilterMenuOpen}
        onClose={handleCloseFilter}
      >
        <Typography
          component="div"
          sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
        >
          Однос
        </Typography>
        {["all", "FAMILY", "FRIEND", "COWORKER", "OTHER"].map(
          (value) => (
            <MenuItem
              key={value}
              selected={relationshipFilter === value}
              onClick={() =>
                applyFilterUpdate(() => setRelationshipFilter(value))
              }
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {relationshipFilter === value ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>
                {value === "all"
                  ? "Сите"
                  : value === "FAMILY"
                  ? "Семејство"
                  : value === "FRIEND"
                  ? "Пријател"
                  : value === "COWORKER"
                  ? "Колега"
                  : "Друго"}
              </ListItemText>
            </MenuItem>
          )
        )}

        <Divider sx={{ my: 1 }} />
        <Typography
          component="div"
          sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
        >
          Возраст
        </Typography>
        {["all", "ADULT", "CHILD", "BABY"].map((value) => (
          <MenuItem
            key={value}
            selected={ageFilter === value}
            onClick={() => applyFilterUpdate(() => setAgeFilter(value))}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {ageFilter === value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>
              {value === "all"
                ? "Сите"
                : value === "ADULT"
                ? "Возрасни"
                : value === "CHILD"
                ? "Деца"
                : "Бебиња"}
            </ListItemText>
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />
        <Typography
          component="div"
          sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
        >
          RSVP статус
        </Typography>
        {["all", "CONFIRMED", "UNKNOWN", "REJECTED"].map((value) => (
          <MenuItem
            key={value}
            selected={rsvpFilter === value}
            onClick={() => applyFilterUpdate(() => setRsvpFilter(value))}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {rsvpFilter === value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>
              {value === "all"
                ? "Сите"
                : value === "CONFIRMED"
                ? "Потврдени"
                : value === "UNKNOWN"
                ? "Неодговорени"
                : "Одбиени"}
            </ListItemText>
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />
        <Typography
          component="div"
          sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
        >
          Седење
        </Typography>
        {["all", "seated", "not-seated"].map((value) => (
          <MenuItem
            key={value}
            selected={seatedFilter === value}
            onClick={() => applyFilterUpdate(() => setSeatedFilter(value))}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {seatedFilter === value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>
              {value === "all"
                ? "Сите"
                : value === "seated"
                ? "Седнати"
                : "Неседнати"}
            </ListItemText>
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />
        <Typography
          component="div"
          sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
        >
          Страна
        </Typography>
        {["all", "BRIDE", "GROOM", "BOTH"].map((value) => (
          <MenuItem
            key={value}
            selected={sideFilter === value}
            onClick={() => applyFilterUpdate(() => setSideFilter(value))}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {sideFilter === value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>
              {value === "all"
                ? "Сите"
                : value === "BRIDE"
                ? "Страна на невестата"
                : value === "GROOM"
                ? "Страна на младоженецот"
                : "Двете страни"}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {loading ? (
        <Box
          className="organizer-list"
          display="flex"
          justifyContent="center"
          p={2}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center" mt={2}>
          {error}
        </Typography>
      ) : filteredGroups.length > 0 ? (
        <>
          <Box className="organizer-list">
            <List>
              {filteredGroups.map(({ mainGuest, sideGuests }) => (
                <GuestCard
                  key={mainGuest.id}
                  mainGuest={mainGuest}
                  sideGuests={sideGuests}
                  tables={tables}
                  setTables={setTables}
                  onEdit={(data) => handleEdit(data)}
                  onDelete={(guestId) => handleDelete(guestId)}
                />
              ))}
            </List>
          </Box>

          <Box
            className="fab-container"
            display="flex"
            justifyContent="flex-end"
          >
            <Fab className="add-fab" onClick={handleAdd}>
              <Add />
            </Fab>
          </Box>
        </>
      ) : (
        <Grid
          container
          spacing={2}
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item>
            <Typography variant="h6" textAlign={{ xs: "center", md: "left" }}>
              {searchQuery || hasActiveFilters
                ? "Нема гости што ги исполнуваат условите."
                : "На копчето додадете гости"}
            </Typography>
          </Grid>
          <Grid item>
            <Fab className="add-fab" onClick={handleAdd}>
              <Add />
            </Fab>
          </Grid>
        </Grid>
      )}

      <GuestModal
        open={modalOpen}
        weddingId={weddingId}
        onClose={() => setModalOpen(false)}
        initialData={selectedGuest}
        hasUnlimitedGuestsProduct={hasUnlimitedGuestsProduct}
        currentTotalGuests={totalGuests}
        guestLimit={guestLimit}
        onSuccess={() => {
          fetchGuests();
          setModalOpen(false);
        }}
      />
    </Grid>
  );
};

export default GuestsTab;
